<!-- h-screen, not min-h-screen: the sidebar and topbar are fixed furniture and
     the content column scrolls inside them. With min-h-screen the aside grows
     with the page and its user footer ends up a thousand pixels below the fold. -->
<div class="h-screen overflow-hidden flex bg-canvas" *transloco="let t">

  <!-- ====================================================================
       Sidebar — the persistent anchor.

       256px expanded for full navigation clarity, 68px icon-only for people
       who already know where everything is. On mobile the same element
       becomes a drawer rather than a second, divergent menu.
       ==================================================================== -->
  <aside [class.md:w-sidebar]="sidebarOpen" [class.md:w-sidebar-collapsed]="!sidebarOpen"
    [class.translate-x-0]="mobileNavOpen" [class.-translate-x-full]="!mobileNavOpen" class="w-sidebar bg-surface border-r border-borderLight flex flex-col shrink-0
           fixed inset-y-0 left-0 z-50 md:static md:translate-x-0
           transition-[width,transform] duration-300 ease-out">

    <!-- Brand -->
    <div class="h-topbar flex items-center px-4 border-b border-borderLight shrink-0">
      <!-- The school's own crest where it has one, the app's mark otherwise:
           a teacher signing in should see their school, not our branding. -->
      <!-- object-contain: a crest keeps its own shape in the square slot
           instead of being stretched to fill it. -->
      <img [src]="brandLogo" alt="" (error)="onLogoError()" class="w-9 h-9 object-contain shrink-0">
      @if (sidebarOpen) {
      <span class="ml-3 font-extrabold text-lg tracking-tight text-slateText
                     overflow-hidden whitespace-nowrap">School36TLabs</span>
      }
      <button (click)="closeMobileNav()" [attr.aria-label]="t('shell.closeNav')"
        class="md:hidden ml-auto p-2 -mr-2 rounded-control text-mutedText hover:bg-gray-50">
        <lucide-icon [img]="X" class="w-5 h-5"></lucide-icon>
      </button>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto kai-scroll py-3 px-2.5 space-y-0.5">
      @for (group of navGroups; track group.heading || $index) {
      @if (group.heading) {
      <div class="pt-4 pb-1">
        @if (sidebarOpen) {
        <p class="px-3 text-label-caps uppercase text-faintText">{{ t(group.heading!) }}</p>
        } @else {
        <div class="mx-2 border-t border-borderLight"></div>
        }
      </div>
      }

      @for (item of group.items; track item.route) {
      <a [routerLink]="item.route" routerLinkActive="kai-nav-item-active"
        [routerLinkActiveOptions]="{ exact: !!item.exact }" (click)="closeMobileNav()" [title]="t(item.label)"
        class="kai-nav-item" [class.md:justify-center]="!sidebarOpen">
        <lucide-icon [img]="item.icon" class="w-5 h-5 shrink-0" [class.mr-3]="sidebarOpen"
          [class.md:mr-0]="!sidebarOpen"></lucide-icon>
        @if (sidebarOpen) {
        <span class="truncate">{{ t(item.label) }}</span>
        }
      </a>
      }
      }

      <div class="pt-4 pb-1">
        <div class="mx-2 border-t border-borderLight"></div>
      </div>

      <a routerLink="/profile" routerLinkActive="kai-nav-item-active" class="kai-nav-item"
        [class.md:justify-center]="!sidebarOpen" [title]="t('shell.myProfile')">
        <lucide-icon [img]="UserRound" class="w-5 h-5 shrink-0" [class.mr-3]="sidebarOpen"
          [class.md:mr-0]="!sidebarOpen"></lucide-icon>
        @if (sidebarOpen) {
        <span class="truncate">{{ t('shell.myProfile') }}</span>
        }
      </a>
    </nav>

    <!-- ------------------------------------------------------------------
         User footer. Who you are signed in as, and in what role, is the one
         piece of context that changes what every other screen will let you
         do — so it stays pinned rather than hidden behind an avatar menu.
         ------------------------------------------------------------------ -->
    <div class="border-t border-borderLight p-2.5 shrink-0">
      <div class="flex items-center gap-3 px-2 py-2 rounded-control" [class.md:justify-center]="!sidebarOpen">
        @if (userPhoto) {
        <img [src]="userPhoto" alt="" (error)="onPhotoError()"
          class="w-9 h-9 rounded-full object-cover bg-primary-tint border border-borderLight shrink-0">
        } @else {
        <div class="w-9 h-9 rounded-full bg-primary-tint text-primary-strong
                      flex items-center justify-center text-sm font-semibold shrink-0">
          {{ userInitials }}
        </div>
        }
        @if (sidebarOpen) {
        <div class="min-w-0 flex-1 leading-tight">
          <p class="text-sm font-semibold text-slateText truncate">{{ userName }}</p>
          <p class="text-label-sm uppercase text-faintText truncate">{{ roleLabel }}</p>
        </div>
        <button type="button" (click)="logout()" [title]="t('shell.signOut')" [attr.aria-label]="t('shell.signOut')"
          class="p-1.5 rounded-control text-mutedText hover:bg-red-50 hover:text-destructive
                   transition-colors shrink-0">
          <lucide-icon [img]="LogOut" class="w-4 h-4"></lucide-icon>
        </button>
        }
      </div>

      <button (click)="toggleSidebar()" [title]="sidebarOpen ? t('shell.collapseNav') : t('shell.expandNav')" class="hidden md:flex w-full items-center justify-center gap-2 mt-1 p-2 rounded-control
               text-mutedText hover:bg-gray-50 hover:text-slateText transition-colors">
        <lucide-icon [img]="Menu" class="w-4 h-4"></lucide-icon>
        @if (sidebarOpen) {
        <span class="text-label-md">{{ t('shell.collapseNav') }}</span>
        }
      </button>
    </div>
  </aside>

  <!-- Level 3 scrim for the mobile drawer. -->
  @if (mobileNavOpen) {
  <div class="kai-scrim md:hidden" (click)="closeMobileNav()"></div>
  }

  <!-- ====================================================================
       Fluid content column
       ==================================================================== -->
  <div class="flex-1 flex flex-col min-w-0">

    <!-- Support context banner: a Super Admin looking at someone else's
         school. Amber, because it is a warning about *where you are*, not
         an error. -->
    @if (supportContext; as ctx) {
    <div class="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-2.5
                  flex flex-wrap items-center justify-between gap-3">
      <p class="text-body-md text-amber-900 flex items-center min-w-0">
        <lucide-icon [img]="ShieldCheck" class="w-4 h-4 mr-2 shrink-0"></lucide-icon>
        <span class="truncate">
          Viewing <strong>{{ ctx.school }}</strong> &middot; {{ ctx.branch }} &middot; {{ ctx.year }}
          in support context. This session is logged.
        </span>
      </p>
      <div class="flex items-center gap-3 shrink-0">
        <span class="text-label-md text-amber-800 tabular-nums">Expires in {{ ctx.minutesLeft }} min</span>
        <button (click)="exitSupportContext()"
          class="text-body-md font-semibold text-amber-900 underline hover:no-underline">Exit context</button>
      </div>
    </div>
    }

    <!-- Top bar -->
    <header class="h-topbar bg-surface border-b border-borderLight shrink-0
                   flex items-center justify-between gap-3 px-4 sm:px-6">

      <div class="flex items-center flex-1 min-w-0 gap-3">
        <button (click)="openMobileNav()" [attr.aria-label]="t('shell.openNav')"
          class="md:hidden p-2 -ml-2 rounded-control text-mutedText hover:bg-gray-50 hover:text-slateText">
          <lucide-icon [img]="Menu" class="w-5 h-5"></lucide-icon>
        </button>

        <!-- Tenant scope. This is the answer to "whose data am I looking
             at", which on a multi-school platform is never rhetorical. For a
             school-wide admin it doubles as the branch switcher. -->
        <div class="hidden sm:flex items-center min-w-0">
          <div class="flex items-center min-w-0 pl-3 pr-1.5 py-1 rounded-control
                      bg-canvas border border-borderLight">
            <div class="w-5 h-5 rounded-full bg-primary-tint text-primary-strong
                        flex items-center justify-center mr-2 shrink-0">
              <lucide-icon [img]="Building2" class="w-3 h-3"></lucide-icon>
            </div>
            @if (isSchoolWideAdmin && branchOptions().length) {
            <span class="text-body-md font-medium text-slateText truncate mr-1">
              {{ auth.user()?.school_name }} &middot;
            </span>
            <select [ngModel]="auth.effectiveBranchId()" (ngModelChange)="switchBranch($event)"
              [disabled]="switchingBranch" [attr.aria-label]="t('shell.branch')"
              class="text-body-md font-medium text-slateText bg-transparent border-0 py-0.5 pl-1 pr-6
                       focus:outline-none focus:ring-1 focus:ring-primary rounded-control cursor-pointer disabled:opacity-60">
              @for (b of branchOptions(); track b.id) {
              <option [ngValue]="b.id">{{ b.name }}</option>
              }
            </select>
            @if (auth.user()?.academic_year_name) {
            <span class="text-body-md text-mutedText truncate ml-1">
              &middot; {{ auth.user()?.academic_year_name }}
            </span>
            }
            } @else {
            <span class="text-body-md font-medium text-slateText truncate pr-1.5">{{ contextLabel }}</span>
            }
          </div>
        </div>
      </div>

      <div class="flex items-center gap-2 sm:gap-3 shrink-0">

        <a routerLink="/announcements" [title]="t('shell.notices')" [attr.aria-label]="t('shell.notices')"
          class="relative p-2 rounded-control text-mutedText hover:bg-gray-50 hover:text-slateText transition-colors">
          <lucide-icon [img]="Bell" class="w-5 h-5"></lucide-icon>
          <span class="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-destructive ring-2 ring-surface"></span>
        </a>

        <app-theme-switcher></app-theme-switcher>

        <app-language-switcher class="hidden sm:inline-flex"></app-language-switcher>

        <div class="h-8 w-px bg-borderLight hidden sm:block"></div>

        <div class="flex items-center gap-2.5">
          <div class="hidden sm:block text-right leading-tight">
            <p class="text-body-md font-semibold text-slateText">{{ userName }}</p>
            <p class="text-label-sm uppercase text-faintText">{{ roleLabel }}</p>
          </div>
          @if (userPhoto) {
          <img [src]="userPhoto" alt="" (error)="onPhotoError()"
            class="w-9 h-9 rounded-full object-cover bg-primary-tint border border-borderLight shrink-0">
          } @else {
          <div class="w-9 h-9 rounded-full bg-primary-tint text-primary-strong
                        flex items-center justify-center text-sm font-semibold shrink-0">
            {{ userInitials }}
          </div>
          }
          <button type="button" (click)="logout()" [title]="t('shell.signOut')" [attr.aria-label]="t('shell.signOut')"
            class="md:hidden p-2 rounded-control text-mutedText hover:bg-red-50 hover:text-destructive transition-colors">
            <lucide-icon [img]="LogOut" class="w-4 h-4"></lucide-icon>
          </button>
        </div>
      </div>
    </header>

    <!-- Page content. Padding steps down on mobile so a data table keeps
         as much of the width as it can. -->
    <main class="flex-1 overflow-y-auto kai-scroll">
      <div class="p-4 sm:p-6 max-w-7xl mx-auto">
        <router-outlet></router-outlet>
        <!-- Raised by any request the school's plan refuses, from any screen. -->
        <app-plan-limit-dialog></app-plan-limit-dialog>
      </div>
    </main>
  </div>
</div>